import {
  AiRecoveryOptions,
  ApplicationVariant,
  AssignedVariableType,
  EXECUTION_STATUS,
  PathType,
  RunnerMode,
} from "./ea.enums";
import {
  AdditionalJobParams,
  ExecutionStep,
  RecorderStep,
  RecorderVariable,
  RunnerClientSessionData,
  RunnerExecutionLogModelTypes,
  RunnerExecutionStepLogModelTypes,
  ScriptDataSourceConfig,
} from "./ea.runner.types";
import {
  Guard,
  KpiLogData,
  Step,
  System,
  TaskScript,
  Variable,
  VirtualUser,
  VirtualUserTokens,
} from "./ea.types";

export const OBFUSCATE_PATTERN = "********" as const;

export type CodeReference = {
  value: any;
  name: string;
  groupName?: string | null;
};

export type RunnerCodeTemplate = CodeReference & {
  params: string[];
  groupName: string;
};

export type RunnerVariable = CodeReference & {
  obfuscate?: boolean;
  dirty?: boolean;
  originalValue?: any;
};

export type RunnerVariableWithId = RunnerVariable & { id: number };

export const CodeReferencesPrefixes = {
  Scripts: "Scripts",
  Script: "Script",
  DataSource: "DataSource",
  Sequence: "Sequence",
  // we need to be compatible with old versions so we transform dynamic to predefined
  Dynamic: "Predefined",
  Global: "Global",
  GlobalVariables: "Global.Variable",
  GlobalConstant: "Global.Constant",
  CodeTemplate: "CodeTemplates",
} as const;

export type CodeReferences = Partial<{
  variables: Partial<{
    dynamic?: Partial<Record<DynamicVariablesKeys, RunnerVariable>>;
    local: Partial<{
      script: Record<string, RunnerVariable>;
      dataSource: Record<string, RunnerVariable>;
      linked: Record<string, RunnerVariable>; // we use groupname to group by linked id
    }>;
    global: Partial<{
      sequence: Record<string, RunnerVariable>; // fetch on usage
      constants: Record<string, RunnerVariable>;
      mutable: Record<string, RunnerVariable>;
      predefined: Record<string, RunnerVariable>;
    }>;
  }>;
  templates: Record<string, RunnerCodeTemplate>;
}>;

export const CodeTypes = {
  CONSTANT: "CONSTANT",
  PICK: "PICK",
  EXPRESSION: "EXPRESSION",
  ADVANCED: "ADVANCED",
  NATIVE: "NATIVE",
  CODE_TEMPLATE: "CODE_TEMPLATE",
  AI_EXTRACT: "AI_EXTRACT",
} as const;

export type CodeType = typeof CodeTypes;

export const DynamicVariables = {
  CurrentStepValue: "CurrentStepValue",
} as const;

export type DynamicVariablesKeys = keyof typeof DynamicVariables;

export type CodeExecutionOptions = Partial<{
  forceObfuscation: boolean;
  dynamicVariables: Partial<Record<DynamicVariablesKeys, any>>;
}>;

export type UsedVariable = {
  id: number;
  linkId?: number;
  type?: AssignedVariableType;
};

export type UsedCodeReferences = {
  variables: UsedVariable[];
  codeTemplates: number[];
};

export type CodeValue = {
  type: keyof CodeType;
  value: string;
  aiInstruction?: string;
  aiSchema?: string;
  assignedVariableId?: number;
  // assignedVariableType?: AssignedVariableType;
  params?: { value: string; type: keyof typeof CodeParamTypes }[];
};

export const CODE_FUNCTION_NAME = "eaExecuteVariableValue" as const;
export const CODE_TEMPLATES_FUNCTION_NAME = "CodeTemplates" as const;

export const CodeParamTypes = {
  CONSTANT: "CONSTANT",
  VARIABLE: "VARIABLE",
} as const;

type BaseCodeExecutionLog = {
  input: string;
  executedCode: string;
};

export type ChangedVariables = Record<
  string,
  {
    name: string;
    value: string;
    type: AssignedVariableType;
  }
>;
export enum CodeExecutionLogType {
  CALCULATE = "CALCULATE",
  ASSIGN = "ASSIGN",
}

export type CalculateCodeExecutionLog = BaseCodeExecutionLog & {
  type: CodeExecutionLogType.CALCULATE;
  output: string;
};

export type AssignCodeExecutionLog = BaseCodeExecutionLog & {
  type: CodeExecutionLogType.ASSIGN;
  changedVariables: ChangedVariables;
  output: Record<string, string>;
};

export type CodeExecutionLog = CalculateCodeExecutionLog | AssignCodeExecutionLog;

export const ROOT_PATH = "root" as const;

export type VersionString = `${number}.${number}.${number}`;

export type CommandBase = {
  id: string;
  value: any;
  version: VersionString;
};

export type CreateCommandDefinition<
  T extends { value: any; id: string },
  Version extends VersionString = "0.0.1",
> = {
  value: T["value"];
  id: T["id"];
  version: Version;
};

export type ExtractCommandValue<T extends CommandBase, K extends T["id"] = T["id"]> = T extends {
  type: K;
  value: infer V;
}
  ? V
  : never;

export type RunnerStep<T extends CommandBase = any> = Omit<
  Step<T>,
  "guards" | "createdAt" | "updatedAt"
> & {
  temporaryStepId?: number | string;
  isDirty?: boolean;
  lastChange?: number;
  path?: string;
  used: {
    variables: UsedVariable[];
    codeTemplates: number[];
    guards: number[];
  };
  frame?: Partial<{
    src: string;
    id: string;
    name: string;
    title: string;
    className: string;
    label: string;
  }>;
  [key: string]: any;
};

export type FlatStep = { id: any; path: string };

export type RunnerVariablesRecord = Record<string, RunnerVariable>;

export type RunnerKpi = Record<number, KpiLogData>;

export type RunnerScriptData = TaskScript & {
  guards: Record<string, Guard>;
  dataSourceConfig: Record<string, ScriptDataSourceConfig>;
  system?: System;
  kpis: RunnerKpi;
};

// WORK: how predefined variables work?

export type RunnerContextData = {
  info: {
    scriptId: number;
  };
  dataSourceLoadTime?: number;
  currentLogId?: string;
  activeVUId?: number;
  hasBeenEverLoaded?: boolean;
  root: number;
  internal?: boolean;
  sessionId: string;
  playerPosition: number;
  recorderPosition: number;
  steps: Record<string, RunnerStep>;
  flat: FlatStep[];
  scripts: Record<string, RunnerScriptData>;
  codeTemplates: Record<string, RunnerCodeTemplate>;
  globalAiRecovery?: AiRecoveryOptions;
  isOptimizer?: boolean;
  variables: {
    local: Record<
      string,
      {
        script: Record<string, RunnerVariable>;
        dataSource: Record<string, RunnerVariable>;
      }
    >;
    global: {
      sequence: Record<string, RunnerVariable>; // fetch on usage
      constants: Record<string, RunnerVariable>;
      mutable: Record<string, RunnerVariable>;
      predefined: Record<string, RunnerVariable>;
    };
  };
};

export type Path = {
  path: string;
  type: PathType;
};

export type ConditionResult =
  | {
      type: "JUMP";
      value: number;
    }
  | {
      type: "KPI";
      value: number[];
    }
  | {
      type: "REPEAT";
    }
  | {
      type: "STOP";
    };

export type ExecutionAction = {
  reload?: boolean;
  overrideStatusTo?: EXECUTION_STATUS;
  preventTermination?: boolean;
  action?: ConditionResult;
};

export const INITIAL_STRUCTURE_VERSION = "0.0.1" as const;

export type RunnerReloadDataRequest = {
  sessionId: string;
};

export type PlayerHeartbeatDataRequest = {
  sessionId: string;
};

export type CommandsTokensValueType = {
  sessionStorage: {
    omit: string[];
    take: string[];
    override: Record<string, any>;
  };
  localStorage: {
    omit: string[];
    take: string[];
    override: Record<string, any>;
  };
  cookies: {
    omit: string[];
    take: string[];
    override: Record<string, any>;
  };
};

export type PlayerHeartbeatDataResponse = {
  alive: boolean;
};

export type RecorderSyncDataRequest = {
  order: (number | string)[];
  removed: number[];
  steps: RunnerStep[];
};

export type RunnerAuthRequest = {
  sessionId: string;
  vuId: number;
  onlyGet?: boolean;
};

export type RunnerAuthResponse = {
  error?: string;
  hasBeenAuthenticated?: boolean;
  tokens?: VirtualUser["tokens"];
  credentials?: {
    login: string;
    password: string;
    domain?: string;
  };
};

export type AdvancedRecorderSyncRequest = {
  steps: ExecutionStep[];
  order?: number[];
  idsToRemove?: number[];
  recordingPath?: string;
};

export type RecorderSyncRequest = {};
export type RunnerInitResponse = { scriptId: number; url: string; mode: RunnerMode };

export type RecorderFinishRequest = {
  steps: RecorderStep[];
  variables: RecorderVariable[];
  order?: number[];
  idsToRemove?: number[];
};

export type RecorderFinishResponse = { steps: Step[]; variables: Variable[] };

export type TokensRequest = {
  virtualUserId: number;
  data: Omit<VirtualUserTokens, "cookies">;
  cookiesSettings: CommandsTokensValueType["cookies"];
};

export type RunnerTokensRequest = {
  sessionId: string;
} & TokensRequest;

export type RunnerTokensResponse = void;

export type RunnerLoadedRequest = {
  sessionId: string;
  file?: File;
};

export type RunnerPlayerEventRequest =
  | {
      type: "START";
      sessionId: string;
    }
  | {
      type: "RESUME";
      sessionId: string;
    }
  | {
      type: "RESET";
      sessionId: string;
    }
  | {
      type: "PAUSE";
      sessionId: string;
    }
  | {
      type: "FINISH";
      sessionId: string;
      mutatedGlobalVariables?: RunnerVariableWithId[];
    }
  | {
      type: "TERMINATE";
      sessionId: string;
    };

export type RunnerLogRequest = {
  sessionId: string;
  log: Partial<
    Pick<
      RunnerExecutionLogModelTypes["general"],
      "id" | "duration" | "iteration" | "state" | "status" | "endTime" | "startTime" | "metadata"
    >
  >;
};

export type RunnerStepLogRequest = {
  sessionId: string;
  transitive?: boolean;
  stepLog: Partial<
    Pick<
      RunnerExecutionStepLogModelTypes["update"],
      | "id"
      | "executionId"
      | "label"
      | "lineNum"
      | "path"
      | "linkedScriptId"
      | "parentStepLogId"
      | "type"
      | "labelKey"
      | "labelParams"
      | "stepData"
      | "startTime"
      | "duration"
      | "endTime"
      | "messages"
      | "status"
      | "variableLogs"
      | "kpi"
      | "metadata"
    >
  >;
};

type OldId = any;
type NewId = number;
export type RecorderSyncDataResponse = {
  idMap: Record<NewId, OldId>;
  steps: RunnerStep[];
};

type Cookies = {
  cookies: Cookie[];
  version: string;
  storeType: string;
  rejectPublicSuffixes: boolean;
};

type Cookie = {
  key: string;
  path: string;
  value: string;
  domain: string;
  secure?: boolean;
  creation: string;
  hostOnly: boolean;
  httpOnly?: boolean;
  lastAccessed: string;
  expires?: string;
  maxAge?: number;
  sameSite?: string;
};

export type Options = {
  enableMultiWindowsSupport: boolean;
  disableWebSecurity: boolean;
};

export type Meta = {
  forceOpenContextMenu?: boolean;
  forceOpenPopup?: boolean;
};

export type RunnerStartResponse = {
  id: string;
  url: string;
  token: string;
  apiUrl: string;
  mode: "RECORDER" | "PLAYER";
  data: Partial<RunnerContextData>;
  session: RunnerClientSessionData;
  additionalStartParams: AdditionalJobParams;
  playerOptions?: { autoPlay?: boolean };
  appVariant?: ApplicationVariant;
  auth?: {
    cookies: Cookies;
    localStorage: string[][];
    sessionStorage: string[][];
  };
  credentials?: {
    login: string;
    password: string;
    domain?: string;
  };
};

export type RunnerInitialDataResponse = Pick<
  RunnerStartResponse,
  | "id"
  | "session"
  | "auth"
  | "data"
  | "url"
  | "mode"
  | "playerOptions"
  | "credentials"
  | "appVariant"
  | "additionalStartParams"
>;

export type InternalStep<T extends CommandBase = any> = Omit<
  RunnerStep<T>,
  "lineNum" | "taskScriptId" | "id"
>;
export const createInternalSteps = (steps: InternalStep[]): InternalStep[] =>
  steps.map((s, index) => ({ ...s, lineNum: index, id: index, taskScriptId: -1 }));
export const createInternalStep = <T extends CommandBase = any>(
  step: InternalStep,
): InternalStep<T> => step;

export type DefaultElementMetadata = {
  id?: string;
  className?: string;
  tagName?: string;
  title?: string;
  textContent?: string;
  role?: string;
  ariaLabel?: string;
  ariaHidden?: string;
  ariaDisabled?: string;
  disabled?: string;
  name?: string;
  type?: string;
};

export type DFOElementMetadata = {
  isDFO?: boolean;
  dataDynControlname?: string;
  dataDynRole?: string;
  dataDynRowId?: string;
  dataDynContainer?: string;
  dataDynSuperTooltip?: string;
  dataDynContent?: string;
  command?: string;
  context?: {
    enabled?: string | boolean;
    visible?: string | boolean;
  };
};
export type CRMElementMetadata = {
  isCRM?: boolean;
  dataType?: string;
  dataText?: string;
  dataId?: string;
  dataLpId?: string;
  dataControlName?: string;
  dataPaNotification?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  dataPreviewOrientation?: string;
  value?: string;
};

export type PlatfromElementMetadata = DFOElementMetadata | CRMElementMetadata;

export type ElementMetadata = DefaultElementMetadata & PlatfromElementMetadata;
